import React, { useCallback, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Cloud,
  CloudCog,
  Circle,
  CircleDot,
  Settings,
  Trash2,
  Loader2,
  Plus
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/errors/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useUserLibraryCategories } from '@/hooks/universal-storage/use-library-categories';
import { useItemCategoryMutations } from '@/hooks/universal-storage/use-library-item-categories';
import { useUniversalStorageItems } from '@/hooks/universal-storage/useUniversalLibraryItems';
import { useGlobalModals } from '@/contexts/GlobalModalsContext';

interface StorageButtonProps {
  itemId: string;
  contentType: 'image' | 'research' | 'workbook' | 'app_idea' | 'private_image' | 'private_research' | 'private_page';
  variant?: 'default' | 'compact' | 'minimal';
  className?: string;
  dropdownAlign?: 'start' | 'center' | 'end';
  
  // Theme customization
  gradientFrom?: string;
  gradientTo?: string;
  accentColor?: string;
  iconColor?: string;
  borderColor?: string;
  hoverBorderColor?: string;
  
  // Content customization
  addText?: string;
  manageText?: string;
  addDescription?: string;
  manageDescription?: string;
  dropdownLabel?: string;
}

const StorageButton: React.FC<StorageButtonProps> = ({
  itemId,
  contentType,
  variant = 'default',
  className,
  dropdownAlign = 'start',
  
  // Theme defaults - purple/blue gradient
  gradientFrom = 'from-purple-50/80 to-blue-50/80 dark:from-purple-950/30 dark:to-blue-950/30',
  gradientTo = 'hover:from-purple-100/80 hover:to-blue-100/80 dark:hover:from-purple-900/40 dark:hover:to-blue-900/40',
  accentColor = 'from-purple-400 to-blue-500',
  iconColor = 'text-purple-600 dark:text-purple-400',
  borderColor = 'border-purple-200/60 dark:border-purple-700/40',
  hoverBorderColor = 'hover:border-purple-300/70 dark:hover:border-purple-600/50',
  
  // Content defaults
  addText = 'Add to Storage',
  manageText = 'Manage Storage',
  addDescription = 'Save this item to your storage',
  manageDescription = 'Item is saved to storage',
  dropdownLabel = 'Add to Storage',
}) => {
  // Storage functionality
  const { openCategoryModal } = useGlobalModals();
  const { data: allCategories, isLoading: isLoadingCategories } = useUserLibraryCategories();
  const { data: storageItems, isLoading: isLoadingStorage } = useUniversalStorageItems();
  const { setCategory, isPending: isStoragePending } = useItemCategoryMutations(itemId, contentType);
  
  // Find current category from storage data
  const currentStorageItem = storageItems?.find(
    item => {
      // Check the appropriate foreign key based on content type
      if (contentType === 'private_image' && item.workbook_image_id === itemId) return true;
      if (contentType === 'private_research' && item.workbook_research_id === itemId) return true;
      if (contentType === 'private_page' && item.workbook_page_id === itemId) return true;
      // For non-private content types, we don't have foreign keys in the new structure
      // These might need different handling depending on your implementation
      return false;
    }
  );
  const currentCategoryId = currentStorageItem?.category_id || null;
  
  // Optimistic state management - track storage status locally
  const [optimisticCategoryId, setOptimisticCategoryId] = useState<string | null>(null);
  const [hasOptimisticUpdate, setHasOptimisticUpdate] = useState(false);

  // Storage state helpers - use optimistic state when available, otherwise use current data
  const isAnythingLoading = isLoadingCategories || isLoadingStorage;
  const effectiveCategoryId = hasOptimisticUpdate ? optimisticCategoryId : currentCategoryId;
  
  // ✅ FIXED: Check if item is in storage (regardless of category)
  const isItemInStorage = currentStorageItem !== undefined;
  const isItemInAnyCategory = effectiveCategoryId !== null;

  // Storage functionality handlers
  const handleCategorySelect = useCallback(async (categoryId: string | null) => {
    if (isStoragePending) return;
    
    // Optimistic update - immediately update local state
    setOptimisticCategoryId(categoryId);
    setHasOptimisticUpdate(true);
    
    try {
      await setCategory(categoryId);
      toast({
        title: "Success",
        description: categoryId ? "Item added to storage" : "Item removed from storage",
      });
    } catch (error) {
      // Revert optimistic update on error
      setOptimisticCategoryId(null);
      setHasOptimisticUpdate(false);
      console.error('Error updating storage category:', error);
      toast({
        title: "Error",
        description: "Failed to update storage",
        variant: "destructive",
      });
    }
  }, [isStoragePending, setCategory]);

  const handleRemoveFromStorage = useCallback(async () => {
    if (isStoragePending) return;
    await handleCategorySelect(null);
  }, [isStoragePending, handleCategorySelect]);

  // Variant-specific styling
  const getVariantStyling = () => {
    switch (variant) {
      case 'compact':
        return {
          container: 'p-3',
          button: 'rounded-lg',
          icon: 'h-4 w-4',
          iconContainer: 'p-1.5',
          title: 'text-sm font-medium',
          description: 'text-xs',
          statusDot: 'w-2 h-2',
          gap: 'gap-2'
        };
      case 'minimal':
        return {
          container: 'p-2',
          button: 'rounded-md',
          icon: 'h-4 w-4',
          iconContainer: 'p-1',
          title: 'text-sm font-medium',
          description: 'hidden',
          statusDot: 'w-2 h-2',
          gap: 'gap-1'
        };
      default:
        return {
          container: 'p-4',
          button: 'rounded-xl',
          icon: 'h-5 w-5',
          iconContainer: 'p-2',
          title: 'font-semibold',
          description: 'text-sm',
          statusDot: 'w-3 h-3',
          gap: 'gap-3'
        };
    }
  };

  const styling = getVariantStyling();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <motion.button
          className={cn(
            "w-full relative overflow-hidden border-2 transition-all duration-300",
            `bg-gradient-to-br ${gradientFrom}`,
            borderColor,
            gradientTo,
            hoverBorderColor,
            "shadow-lg hover:shadow-xl",
            "group active:scale-[0.98]",
            styling.button,
            className
          )}
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          disabled={isAnythingLoading}
        >
          {/* Subtle top accent gradient */}
          <div className={cn("absolute top-0 left-0 right-0 h-1 bg-gradient-to-r", accentColor)} />
          
          <div className={styling.container}>
            <div className={cn("flex items-center", styling.gap)}>
              {/* Icon */}
              <div className={cn(
                "flex-shrink-0 rounded-lg transition-colors duration-300",
                "bg-white/60 dark:bg-white/10",
                "group-hover:bg-white/80 dark:group-hover:bg-white/15",
                styling.iconContainer
              )}>
                {isItemInStorage ? (
                  <CloudCog className={cn(styling.icon, iconColor)} />
                ) : (
                  <Cloud className={cn(styling.icon, iconColor)} />
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2">
                  <h5 className={cn("text-gray-900 dark:text-gray-100", styling.title)}>
                    {isItemInStorage ? manageText : addText}
                  </h5>
                  {isAnythingLoading && (
                    <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
                  )}
                </div>
                {variant !== 'minimal' && (
                  <p className={cn(
                    "text-gray-600 dark:text-gray-400 mt-0.5",
                    styling.description
                  )}>
                    {isItemInStorage ? manageDescription : addDescription}
                  </p>
                )}
              </div>
              
              {/* Status indicator */}
              <div className="flex-shrink-0">
                {isItemInStorage ? (
                  <div className={cn("rounded-full bg-green-500 dark:bg-green-400 shadow-sm", styling.statusDot)} />
                ) : (
                  <Plus className="h-4 w-4 text-gray-400" />
                )}
              </div>
            </div>
          </div>
        </motion.button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align={dropdownAlign} className="w-64" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel>{dropdownLabel}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {isAnythingLoading ? (
          <div className="flex items-center justify-center p-2">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : (
          <>
            {/* Category options */}
            {allCategories && allCategories.length > 0 ? (
              <div className="max-h-60 overflow-y-auto">
                {allCategories.map(cat => {
                  const isSelected = effectiveCategoryId === cat.id;
                  return (
                    <DropdownMenuItem 
                      key={cat.id} 
                      onSelect={(e) => e.preventDefault()} 
                      onClick={() => handleCategorySelect(cat.id)} 
                      disabled={isStoragePending}
                      className="flex items-center gap-2"
                    >
                      {isSelected ? (
                        <CircleDot className="h-4 w-4 text-purple-500" />
                      ) : (
                        <Circle className="h-4 w-4 text-gray-400" />
                      )}
                      <span className="flex-grow">{cat.name}</span>
                    </DropdownMenuItem>
                  );
                })}
              </div>
            ) : (
              <p className="px-2 py-1.5 text-sm text-muted-foreground">No folders found.</p>
            )}
          </>
        )}
        
        {/* Remove from Storage option - only show if item is in storage */}
        {isItemInStorage && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onSelect={(e) => { e.preventDefault(); handleRemoveFromStorage(); }}
              disabled={isStoragePending}
              className="text-red-600 dark:text-red-400 focus:text-red-600 dark:focus:text-red-400"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              <span>Remove</span>
            </DropdownMenuItem>
          </>
        )}
        
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={(e) => { e.preventDefault(); openCategoryModal(); }}>
          <Settings className="mr-2 h-4 w-4" />
          <span>Manage Storage</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default StorageButton; 